/* Predictive search — CRO-optimized.
   - Locale-aware suggest fetches (Shopify Markets URL prefixes)
   - Debounce + AbortController (no stale-result races) + per-term cache
   - Loading state, min 2 characters
   - Recent searches (localStorage) in the zero state
   - Full keyboard navigation (arrows / Enter / Escape) with ARIA combobox
   - Sold-out products demoted by the suggest API */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="text"]');
    this.overlay = this.querySelector('.predictive-search-overlay');
    this.zeroState = this.querySelector('.search-zero-state');
    this.injectionDiv = this.querySelector('.predictive-search-injection');
    this.form = this.querySelector('form');
    this.recentWrap = this.querySelector('[data-recent-searches]');
    this.recentList = this.querySelector('[data-recent-list]');

    if (!this.input || !this.overlay) return;

    this.root = (this.getAttribute('data-root-url') || '/').replace(/\/$/, '');
    this.cache = new Map();
    this.abortController = null;
    this.activeIndex = -1;

    this.STORAGE_KEY = 'sw_recent_searches';

    /* ARIA combobox semantics */
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');

    this.input.addEventListener('input', this.debounce(() => this.onChange(), 250));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeyDown.bind(this));

    if (this.form) {
      this.form.addEventListener('submit', () => {
        this.saveRecent(this.input.value.trim());
      });
    }

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.close();
    });

    for (const pill of this.querySelectorAll('.trending-pill')) {
      pill.addEventListener('click', () => {
        this.input.value = pill.getAttribute('data-query') || pill.textContent.trim();
        this.input.focus();
        this.onChange();
      });
    }

    const clearBtn = this.querySelector('[data-recent-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        try { localStorage.removeItem(this.STORAGE_KEY); } catch (err) {}
        this.renderRecent();
        this.input.focus();
      });
    }

    /* Save the term when a result is clicked, so it shows up in recents */
    this.overlay.addEventListener('click', (e) => {
      if (e.target.closest('[data-ps-option]')) {
        this.saveRecent(this.input.value.trim());
      }
    });

    this.renderRecent();
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /* ── Recent searches ── */
  getRecent() {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  saveRecent(term) {
    if (!term || term.length < 2) return;
    try {
      let list = this.getRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      if (list.length > 5) list = list.slice(0, 5);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(list));
    } catch (e) {}
    this.renderRecent();
  }

  renderRecent() {
    if (!this.recentWrap || !this.recentList) return;
    const list = this.getRecent();
    this.recentList.textContent = '';
    if (list.length === 0) {
      this.recentWrap.hidden = true;
      return;
    }
    for (const term of list) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'trending-pill trending-pill--recent';
      btn.textContent = term; /* textContent: stored strings never parsed as HTML */
      btn.addEventListener('click', () => {
        this.input.value = term;
        this.input.focus();
        this.onChange();
      });
      this.recentList.appendChild(btn);
    }
    this.recentWrap.hidden = false;
  }

  /* ── Open/close/state ── */
  onFocus() {
    this.open();
    if (this.input.value.trim().length < 2) this.showZeroState();
  }

  open() {
    this.overlay.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.overlay.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  }

  showZeroState() {
    if (this.zeroState) this.zeroState.style.display = 'block';
    this.injectionDiv.style.display = 'none';
  }

  showResults() {
    if (this.zeroState) this.zeroState.style.display = 'none';
    this.injectionDiv.style.display = 'block';
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    this.setActive(-1);

    if (searchTerm.length < 2) {
      this.injectionDiv.innerHTML = '';
      this.showZeroState();
      this.open();
      return;
    }

    this.showResults();
    this.open();
    this.getSearchResults(searchTerm);
  }

  /* ── Fetch (cached, abortable, locale-aware) ── */
  async getSearchResults(searchTerm) {
    const key = searchTerm.toLowerCase();
    if (this.cache.has(key)) {
      this.injectionDiv.innerHTML = this.cache.get(key);
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.overlay.classList.add('is-loading');

    try {
      const params =
        'q=' + encodeURIComponent(searchTerm) +
        '&resources[type]=product,query,collection' +
        '&resources[limit]=10&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        '&section_id=predictive-search';
      const response = await fetch(this.root + '/search/suggest?' + params, {
        signal: this.abortController.signal,
      });
      if (!response.ok) return;

      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const section = parsed.querySelector('#shopify-section-predictive-search');

      if (section && this.input.value.trim().toLowerCase() === key) {
        this.injectionDiv.innerHTML = section.innerHTML;
        this.cache.set(key, section.innerHTML);
        if (this.cache.size > 40) {
          this.cache.delete(this.cache.keys().next().value);
        }
      }
    } catch (error) {
      /* aborted or offline — keep whatever is shown */
    } finally {
      this.overlay.classList.remove('is-loading');
    }
  }

  /* ── Keyboard navigation ── */
  options() {
    return Array.from(this.overlay.querySelectorAll('[data-ps-option]')).filter(
      (el) => el.offsetParent !== null
    );
  }

  setActive(index) {
    const opts = this.options();
    for (const o of opts) o.classList.remove('is-active');
    this.activeIndex = index;
    if (index >= 0 && opts[index]) {
      opts[index].classList.add('is-active');
      opts[index].scrollIntoView({ block: 'nearest' });
    }
  }

  onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
      this.input.blur();
      return;
    }
    if (this.overlay.style.display !== 'block') return;
    const opts = this.options();
    if (opts.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setActive((this.activeIndex + 1) % opts.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setActive((this.activeIndex - 1 + opts.length) % opts.length);
    } else if (e.key === 'Enter' && this.activeIndex >= 0 && opts[this.activeIndex]) {
      e.preventDefault();
      this.saveRecent(this.input.value.trim());
      opts[this.activeIndex].click();
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
